import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { FaCheck } from 'react-icons/fa'
import { FiCalendar, FiCreditCard, FiFileText } from 'react-icons/fi'
import { Button } from 'src/components/button'
import { TriStateCheckbox } from 'src/components/bulk'
import { ListItemCard, ListItemRow } from 'src/components/layout/item-collection'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { ExpenseGroup } from 'src/services/expenses'
import { downloadFile } from 'src/utils/download'
import { cn } from 'src/utils/style'
import { getCurrencyValue, getTimeAgo } from 'src/utils/utils'

interface ExpenseListItemProps {
  expense: ExpenseGroup
  viewMode: ViewMode
  isSelected: boolean
  onToggleSelect: () => void
  onClick: () => void
}

export const ExpenseListItem = ({
  expense,
  viewMode,
  isSelected,
  onToggleSelect,
  onClick,
}: ExpenseListItemProps) => {
  const { t } = useTranslation()
  const receiptCount = expense.receiptsRef?.length || 0
  const reportNames = expense.reportsRef?.map((r) => r.name).join(', ') ?? ''

  const checkbox = (className?: string) => (
    <TriStateCheckbox
      state={isSelected ? 'checked' : 'unchecked'}
      onChange={onToggleSelect}
      aria-label={t('bulkActions.selectRow', { name: expense.name })}
      data-testid={`bulk-select-row-${expense.id}`}
      className={className}
    />
  )

  const receiptBadge = (
    <span
      className={cn([
        'shrink-0 rounded px-2 py-1 text-xs font-semibold',
        receiptCount > 0 ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-text-placeholder',
      ])}
      title={t('expensesPage.receipts')}
      data-testid={`expense-${expense.id}-receipt-count`}
    >
      <FiFileText className="inline h-3 w-3" /> {receiptCount}
    </span>
  )

  const amount = (
    <span
      className={cn([
        'truncate text-lg font-semibold',
        expense.cashFlowType === 'credit' ? 'text-green-600' : 'text-error',
      ])}
    >
      {expense.cashFlowType === 'credit' ? '+' : '-'}
      {getCurrencyValue(expense?.amount, expense?.amountCurrency)}
    </span>
  )

  const valueActions = (
    <>
      {amount}
      {expense.isReconciled && (
        <FaCheck
          className="h-3.5 w-3.5 shrink-0 text-green-500"
          title={t('expensesPage.reconciled')}
        />
      )}
      {receiptCount > 0 && (
        <Button
          variant="ghost"
          tone="neutral"
          iconOnly
          size="sm"
          type="button"
          tooltip={t('expensesPage.downloadReceipt', 'Download receipt')}
          data-testid={`expense-${expense.id}-download-receipt`}
          onClick={(e) => {
            e.stopPropagation()
            const receipt = expense.receiptsRef?.[0]
            if (receipt?.asset) downloadFile(receipt.asset, receipt.name)
          }}
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
        </Button>
      )}
    </>
  )

  if (viewMode === 'list') {
    return (
      <ListItemRow onClick={onClick} data-testid={`expense-row-${expense.id}`}>
        {checkbox('self-start @[480px]:self-center')}
        <div className="min-w-0 flex-1">
          <div className="flex flex-col @[480px]:flex-row @[480px]:items-center @[480px]:gap-2">
            <h3 className="text-left font-semibold">{expense.name}</h3>
            {expense.organizationRef && (
              <span className="text-sm text-text-secondary">• {expense.organizationRef.name}</span>
            )}
          </div>
          <div className="flex flex-col gap-1 text-sm text-text-placeholder @[480px]:flex-row @[480px]:items-center @[480px]:gap-2">
            <span>
              <FiCalendar className="inline h-4 w-4" />{' '}
              {getTimeAgo(expense.expenseDate ?? undefined)} •{' '}
              <FiCreditCard className="inline h-4 w-4" /> {expense.cashFlowType}
            </span>
            <div className="flex items-center gap-2">
              {expense.categoryRef && (
                <span className="rounded bg-primary-200 px-2 py-1 text-xs font-semibold text-primary-800">
                  {expense.categoryRef.label}
                </span>
              )}
              {reportNames && (
                <span className="rounded bg-accent-200 px-2 py-1 text-xs font-semibold text-accent-800">
                  {reportNames}
                </span>
              )}
              {receiptBadge}
            </div>
          </div>
        </div>
        <div className="relative mt-2 flex items-center gap-2 @[480px]:mt-0">{valueActions}</div>
      </ListItemRow>
    )
  }

  return (
    <ListItemCard onClick={onClick} data-testid={`expense-card-${expense.id}`}>
      {/* Header */}
      <div className="flex min-w-0 items-start gap-3">
        {checkbox('mt-1.5 shrink-0')}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-bold text-text-primary" title={expense.name}>
            {expense.name}
          </h3>
          {expense.organizationRef && (
            <p
              className="truncate text-sm text-text-placeholder"
              title={expense.organizationRef.name}
            >
              {expense.organizationRef.name}
            </p>
          )}
        </div>
        {receiptBadge}
      </div>

      {/* Key fields */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('expensesPage.date')}</dt>
          <dd className="truncate font-medium text-text-primary">
            {getTimeAgo(expense.expenseDate ?? undefined)}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('expensesPage.cashFlow')}</dt>
          <dd className="truncate font-medium capitalize text-text-primary">
            {expense.cashFlowType || '-'}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('expensesPage.category')}</dt>
          <dd
            className="truncate font-medium text-text-primary"
            title={expense.categoryRef?.label || undefined}
          >
            {expense.categoryRef?.label || '-'}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('expensesPage.report')}</dt>
          <dd className="truncate font-medium text-text-primary" title={reportNames || undefined}>
            {reportNames || '-'}
          </dd>
        </div>
      </dl>

      {/* Amount */}
      <div className="mt-auto flex min-w-0 items-center justify-between gap-3 border-t border-border pt-3">
        <span className="shrink-0 text-sm text-text-secondary">{t('expensesPage.amount')}</span>
        <div className="flex min-w-0 items-center justify-end gap-2">{valueActions}</div>
      </div>
    </ListItemCard>
  )
}
